"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, Compass, FileText, FlaskConical, Layers, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const knowledgeCentres = [{
  title: "Powder Testing Knowledge Centre",
  description: "Technical guidance, compendial standards, and flowability characterisation methodologies for pharmaceutical powders.",
  link: "/PowderTestingKnowledgeCentrePage",
  icon: Layers,
  tag: "Powder Characterisation"
}, {
  title: "Raw Material Identification",
  description: "Articles and practical notes on rapid identity verification, handheld Raman spectroscopy, and pharmacopoeial compliance.",
  link: "/RawMaterialIdentificationKnowledgeCentrePage",
  icon: FlaskConical,
  tag: "Raw Materials"
}, {
  title: "HPLC Sustainability & Solvent Recovery",
  description: "Benchmarking solvent reduction, automated recovery strategies, and green analytical chemistry practices for chromatography labs.",
  link: "/HplcSustainabilityKnowledgeCentrePage",
  icon: Compass,
  tag: "Sustainability"
}, {
  title: "Safety & Security Knowledge Centre",
  description: "Operational protocols, field chemical identification guidelines, and narcotics/hazmat detection technical documentation.",
  link: "/SafetySecurityKnowledgeCentrePage",
  icon: ShieldCheck,
  tag: "Safety & Defence"
}];
function LatestPostsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(i => <Card key={i} data-index={i} className="border-border bg-card shadow-xs">
            <CardHeader className="space-y-3 pb-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-full" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </CardContent>
            <CardFooter className="pt-2">
              <Skeleton className="h-8 w-28" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center shadow-xs">
        <p className="text-sm text-muted-foreground">
          Unable to load recent articles.
        </p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-dashed border-border bg-secondary/40 p-10 text-center">
        <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-base font-medium text-foreground">
          No posts available yet
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Please check back soon for the latest technical bulletins and application updates.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const formattedDate = post.date ? new Date(post.date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
      }) : null;
      return <Card key={post.id} data-index={i} className="group flex flex-col justify-between border-border bg-card transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
            <div>
              {post.featuredImage && <div className="overflow-hidden rounded-t-xl">
                  <img src={post.featuredImage} alt={post.title || "Article thumbnail"} loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>}
              <CardHeader className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                  <span>{formattedDate || "Technical Bulletin"}</span>
                </div>
                <CardTitle className="line-clamp-2 text-lg font-semibold text-foreground group-hover:text-primary">
                  {post.title}
                </CardTitle>
              </CardHeader>
              {post.excerpt && <CardContent>
                  <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{
              __html: post.excerpt.replace(/<[^>]+>/g, "")
            }} />
                </CardContent>}
            </div>
            <CardFooter className="pt-2">
              <Button asChild variant="ghost" size="sm" className="group/btn -ml-2 px-2 text-primary hover:bg-secondary hover:text-primary">
                <Link to={post.link || "#"}>
                  <span>Read full article</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                </Link>
              </Button>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function Resources() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="resources" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container space-y-16">
        {/* Section Heading */}
        <div className="max-w-3xl space-y-3">
          <Badge variant="secondary" className="px-3 py-1 text-xs">
            Knowledge & Insights
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Technical Knowledge Hub
          </h2>
          <p className="text-base text-muted-foreground sm:text-lg">
            Access application notes, regulatory insights, and instrumentation guidance
            prepared by our analytical science specialists for teams across Ireland and the UK.
          </p>
        </div>

        {/* Knowledge Centre Cards Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h3 className="text-xl font-semibold text-foreground">
                Specialist Knowledge Centres
              </h3>
            </div>
            <Button asChild variant="link" className="p-0 text-sm">
              <Link to="/ResourcesPage">
                <span>View all resources</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Featured Primary Card */}
            <Card className="relative flex flex-col justify-between border-primary/30 bg-secondary/50 p-6 shadow-xs lg:col-span-1">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge variant="default" className="bg-primary text-primary-foreground">
                    Featured Hub
                  </Badge>
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-foreground">
                    PAT Knowledge Centre
                  </h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Comprehensive resources covering Process Analytical Technology,
                    in-line NIR monitoring, chemometrics, and real-time release testing
                    for pharmaceutical and biopharmaceutical manufacturing.
                  </p>
                </div>
              </div>
              <div className="pt-6">
                <Button asChild className="w-full">
                  <Link to="/PatKnowledgeCentrePage">
                    <span>Explore PAT Knowledge</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </Card>

            {/* Secondary Knowledge Centre Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2">
              {knowledgeCentres.map((item, i) => {
              const IconComponent = item.icon;
              return <Card key={item.link} data-index={i} className="flex flex-col justify-between border-border bg-card p-5 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {item.tag}
                        </Badge>
                        <IconComponent className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-foreground">
                          {item.title}
                        </h4>
                        <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="pt-4">
                      <Button asChild variant="ghost" size="sm" className="group/item -ml-2 px-2 text-xs font-medium text-primary hover:bg-secondary hover:text-primary">
                        <Link to={item.link}>
                          <span>Open Knowledge Hub</span>
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/item:translate-x-0.5" />
                        </Link>
                      </Button>
                    </div>
                  </Card>;
            })}
            </div>
          </div>
        </div>

        {/* Latest Articles Section */}
        <div className="space-y-6 pt-4">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div>
              <h3 className="text-xl font-semibold text-foreground">
                Latest Articles & Updates
              </h3>
              <p className="text-sm text-muted-foreground">
                Recent industry insights, regulatory analysis, and application studies.
              </p>
            </div>
          </div>

          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date",
          status: ["publish"]
        }}>
            <LatestPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>;
}